import { HttpInterceptor, HttpRequest, HttpErrorResponse, HttpHandler, HttpEvent } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { AUTH_ERRORS } from '@aston/foundation';
import { Router } from '@angular/router';

import * as RoutesDefinitions from '../../routes-definitions';
import { AuthenticationService } from '../services';

@Injectable({providedIn: 'root'})
export class AuthInterceptor implements HttpInterceptor {
	tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

	private authenticationService = inject(AuthenticationService)
	private router = inject(Router)

	// http://ericsmasal.com/2018/07/02/angular-6-with-jwt-and-refresh-tokens-and-a-little-rxjs-6/
	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

		// do not send authentication info for assets and connection request
		if (req.url.includes('/assets') || req.url.includes('connect/token')) {
			return next.handle(req);
		}

		return this.authenticationService.getAccessToken().pipe(
			switchMap((token: string) => next.handle(this.addTokenToRequest(req, token))),
			catchError((err: any) => {
				switch ((err as HttpErrorResponse).status) {
				case 400:
					return this.handle400Error(err);
				case 0:
				case 401:
					if (err.error?.SpecificError === AUTH_ERRORS.LoggedInUserDoesNotExists ||
							err.originalError?.error?.SpecificError === AUTH_ERRORS.LoggedInUserDoesNotExists) {
						this.authenticationService.logout();
						return throwError(err);
					}
					if (err.error?.SpecificError === AUTH_ERRORS.UserSignedOut ||
							err.originalError?.error?.SpecificError === AUTH_ERRORS.UserSignedOut) {
						this.authenticationService.logout();
						return throwError(err);
					}
					return this.handle401Error(req, next);
				default:
					return throwError(err);
				}
			}
			));
	}

	addTokenToRequest(req: HttpRequest<any>, token: string): HttpRequest<any> {
		return req.clone({
			setHeaders: this.authHeaders(token)
		});
	}

	getAuthHeaders(): Observable<Record<string, string>> {
		return this.authenticationService.getAccessToken().pipe(map(token => this.authHeaders(
			token,
		)))
	}

	private authHeaders(token: string): Record<string, string> {
		return {
			Authorization: `Bearer ${token}`,
		}
	}

	handle400Error(error) {

		if (error && error.status === 400 && error.error && error.error.error === 'invalid_grant') {
			// If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
			return this.logoutUser();
		}

		return throwError(error);
	}

	handle401Error(req: HttpRequest<any>, next: HttpHandler) {
		// TODO: What should we do ?
		return next.handle(req);
	}

	logoutUser() {
		this.authenticationService.logout();
		this.router.navigate([RoutesDefinitions.getLoginFullPath()]);
		return throwError('');
	}
}
